import { useEffect, useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Sayz — Impact-aware delete confirmation.
 *
 * Panel'in tüm silme onayları için TEK kaynak. Kullanıcı Görsel silme
 * popup'ının UI/UX'ini beğendi ve Model + Tablo silme onaylarının da
 * aynı görsel dile taşınmasını istedi. Bağlantısız (impactCount=0) bir
 * entity silinirken de aynı popup, sadece amber uyarı ve ack checkbox
 * gizlenir.
 *
 * Anatomi:
 *  ┌─────────────────────────────────────────────┐
 *  │ 🗑  "İsim" silinsin mi?                     │  <— title + trash icon
 *  ├─────────────────────────────────────────────┤
 *  │ ⚠  N ürün bu {entity}yi kullanıyor. ...   │  <— amber banner (inUse only)
 *  │ {permanentNote}                             │  <— body kalıcı silme notu
 *  │ ☐  Anladım — {ackMessage}                   │  <— ack (inUse only, zorunlu)
 *  │                        [Vazgeç] [Evet, sil] │
 *  └─────────────────────────────────────────────┘
 *
 * Kullanım (Görsel örneği):
 *   <DeleteConfirmDialog
 *      open={!!target}
 *      onOpenChange={(o) => !o && setTarget(null)}
 *      itemName={target?.name}
 *      impactCount={n}
 *      impactMessage={<><strong>{n} ürün</strong> bu görseli kullanıyor. ...</>}
 *      permanentNote="Görsel dosyası ve tüm versiyon geçmişi kalıcı olarak silinir. Bu işlem geri alınamaz."
 *      ackMessage={<><strong>{n} ürün</strong> silme sonrası görselsiz kalacak.</>}
 *      onConfirm={handleDelete}
 *      testId="images-delete"
 *   />
 */
export function DeleteConfirmDialog({
    open,
    onOpenChange,
    itemName,
    impactCount = 0,
    impactMessage,
    permanentNote,
    ackMessage,
    onConfirm,
    testId = "delete",
    confirmLabel = "Evet, sil",
    cancelLabel = "Vazgeç",
}) {
    const [ack, setAck] = useState(false);

    // Reset the ack checkbox each time the dialog closes so a second delete
    // attempt on another entity starts unchecked.
    useEffect(() => {
        if (!open) setAck(false);
    }, [open]);

    const inUse = impactCount > 0;

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent data-testid={`${testId}-dialog`}>
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                        <Trash2 className="h-4 w-4 text-red-600" />
                        &quot;{itemName}&quot; silinsin mi?
                    </AlertDialogTitle>
                    {/* asChild wraps a <div> so we can freely compose block-level
                        children (banner + note) without violating <p> rules. */}
                    <AlertDialogDescription asChild>
                        <div className="space-y-3">
                            {inUse && impactMessage && (
                                <div
                                    className="flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-50 p-3 text-amber-900"
                                    data-testid={`${testId}-impact`}
                                >
                                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                                    <span>{impactMessage}</span>
                                </div>
                            )}
                            <div className="text-muted-foreground">{permanentNote}</div>
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>

                {inUse && ackMessage && (
                    <label
                        className="flex items-start gap-2 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer"
                        data-testid={`${testId}-ack-label`}
                    >
                        <Checkbox
                            checked={ack}
                            onCheckedChange={(v) => setAck(!!v)}
                            className="mt-0.5"
                            data-testid={`${testId}-ack`}
                        />
                        <span className="text-xs leading-relaxed">
                            <strong>Anladım</strong> — {ackMessage}
                        </span>
                    </label>
                )}

                <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`${testId}-cancel`}>{cancelLabel}</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={onConfirm}
                        disabled={inUse && !ack}
                        className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:pointer-events-none"
                        data-testid={`${testId}-confirm`}
                    >
                        {confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
